import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import headerImg from "./back.jpg";
import { Icon } from "@iconify/react";
import useResponsive from "../../../../../Hooks/useResponsive";
import { useSelector } from "react-redux";
import MotionWrapper from "../../../../../components/MotionWrapper";
import React, { useMemo, useCallback } from "react";

const UserProfile = ({ formik }) => {
  const { values, setFieldValue } = formik;
  const { userID, name, photo, designationName, departmentName } =
    values?.user || {};

  const { dataUserUnder } = useSelector((state) => state.general);

  const smUp = useResponsive("up", "sm");

  const handleChanges = useCallback(
    (event, newValue) => {
      setFieldValue("user", newValue);
    },
    [setFieldValue]
  );

  // Memoize user options
  const userOptions = useMemo(
    () => [{ userID: -1, name: "View All" }, ...dataUserUnder],
    [dataUserUnder]
  );

  // Memoized AvatarGroup or Avatar
  const renderAvatar = useMemo(() => {
    if (name === "View All") {
      return (
        <MotionWrapper directions={"bottom"} delay={0.4}>
          <AvatarGroup max={4}>
            {dataUserUnder.map((user, i) => (
              <Avatar
                key={user.userID || i}
                alt={user.name}
                src={`https://workspace.teaminterval.net/assets/employee/photo/passport_size/${user.photo}.jpg`}
              />
            ))}
          </AvatarGroup>
        </MotionWrapper>
      );
    }

    return (
      <MotionWrapper directions={"right"} delay={0.5}>
        <Avatar
          src={`https://workspace.teaminterval.net/assets/employee/photo/passport_size/${photo}.jpg`}
          alt="profile"
        />
      </MotionWrapper>
    );
  }, [name, dataUserUnder, photo]);

  // Memoized chip label
  const chipLabel = useMemo(() => {
    return name === "View All"
      ? "Team All Users"
      : designationName || "designationName";
  }, [name, designationName]);

  // Memoized display name
  const displayName = useMemo(() => {
    return name === "View All"
      ? dataUserUnder[1]?.departmentName
      : name || "name not available";
  }, [name, dataUserUnder]);

  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          padding: 2,
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            height: 90,
            backgroundImage: `url(${headerImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            borderRadius: 3,
          }}
        >
          <Box sx={{ position: "relative", bottom: -40, p: 3 }}>
            <Stack direction={"row"}>
              <Box>{renderAvatar}</Box>
            </Stack>
            <Box sx={{ ml: 1 }}>
              <Typography variant="h6" sx={{ fontSize: smUp ? undefined : 13 }}>
                {displayName}
              </Typography>
              <Box>
                <Chip
                  icon={
                    <Icon
                      icon="eos-icons:project-outlined"
                      width="15"
                      height="15"
                    />
                  }
                  size="small"
                  variant="outlined"
                  label={chipLabel}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 10,
          }}
        >
          <Autocomplete
            disablePortal
            size="small"
            options={userOptions}
            value={values.user}
            onChange={handleChanges}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) =>
              option.userID === value.userID
            }
            sx={{ width: "60%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="ic:round-person" width="24" height="24" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "15px",
                  },
                }}
              />
            )}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default React.memo(UserProfile);
